/**
 * Config Context
 * Provides access to TraceKitConfig throughout the application
 */

import React, { createContext, useContext, ReactNode } from 'react';
import { TraceKitConfig } from '../types/config';

interface ConfigContextType {
  config: TraceKitConfig;
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export function ConfigProvider({ 
  config, 
  children 
}: { 
  config: TraceKitConfig; 
  children: ReactNode;
}) {
  return (
    <ConfigContext.Provider value={{ config }}>
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig(): TraceKitConfig {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within ConfigProvider');
  }
  return context.config;
}

/** Returns the config or undefined when used outside a ConfigProvider (e.g. tests). */
export function useOptionalConfig(): TraceKitConfig | undefined {
  const context = useContext(ConfigContext);
  return context?.config;
}

/**
 * Hook to check if a feature flag is enabled
 * Returns true if the flag is explicitly set to true, false otherwise
 * Defaults to true if not specified (backward compatibility)
 */
export function useFeatureFlag(flagName: keyof NonNullable<TraceKitConfig['customization']>['featureFlags']): boolean {
  const config = useConfig();
  const flag = config.customization?.featureFlags?.[flagName];
  // Default to true if not specified (backward compatibility)
  return flag !== undefined ? flag : true;
}
